import L from 'leaflet'
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import commercialSvg from '../../svgs/listing-commercial.svg?raw'
import offerSvg from '../../svgs/listing-offer.svg?raw'
import requestSvg from '../../svgs/listing-request.svg?raw'
import { Dialog } from './dialog'
import { Position } from 'geojson'
import { buildListingFilterQueryParams, fetchListings, Listing } from '../lib/api'
import { calculateCentroid } from '../lib/geo'
import { setupCooperativeGestures } from '../lib/leaflet-cooperative-gestures'
import { initializeListingTeasers } from './listing-teaser'
import { ListingsFilter } from './listings-filter'

const iconSvgMap = {
  offer: offerSvg,
  request: requestSvg,
  commercial: commercialSvg
} as const

// Extend MarkerOptions type to include a primaryCategoryColor property
type ListingMarkerOptions = L.MarkerOptions & {
  primaryCategoryColor: string
}

// Extend Marker type to include the previously extended options type
type ListingMarker = L.Marker & {
  options: ListingMarkerOptions
}

type ListingFilters = {
  type?: string[]
  category?: string[]
  region?: string[]
}

export class ListingsSection {
  private $element: HTMLElement
  private $itemsList: HTMLElement
  private $zoomInControl: HTMLButtonElement | null = null
  private $zoomOutControl: HTMLButtonElement | null = null
  private $subscribeForm: HTMLDivElement | null = null
  private $subscribeFiltersInput: HTMLInputElement | null = null

  private map: ReturnType<typeof L.map> | null = null
  private markerLayer: L.LayerGroup | null = null
  private iconsMap: Record<string, ReturnType<typeof L.divIcon>> = {}

  private listingsFilter: ListingsFilter | null = null

  private currentPage: number = 1
  private lastListHref: string
  private listings: Listing[] = []
  private loadingListings: boolean = false
  private listingMarkerMap: Map<number, ListingMarker> = new Map()

  private filtersDirty: boolean = false
  private filters: ListingFilters = {}

  private isZoomModifierKeyPressed: boolean = false
  private scrollMessageTimeout: number | null = null

  constructor($element: HTMLElement) {
    this.$element = $element
    this.lastListHref = location.href

    // Instantiate ListingsFilter child component
    const $listingsFilter = this.$element.querySelector('.listings-filter')
    if ($listingsFilter !== null) {
      this.listingsFilter = new ListingsFilter(this, $listingsFilter)
    }

    // Logic for zoom in control
    this.$zoomInControl = this.$element.querySelector('.section-listings__control--zoom-in')
    if (this.$zoomInControl !== null) {
      this.$zoomInControl.addEventListener('click', () => {
        this.map?.zoomIn()
      })
    }

    // Logic for zoom out control
    this.$zoomOutControl = this.$element.querySelector('.section-listings__control--zoom-out')
    if (this.$zoomOutControl !== null) {
      this.$zoomOutControl.addEventListener('click', () => {
        this.map?.zoomOut()
      })
    }

    this.$subscribeForm =
      this.$element.querySelector('.section-listings__subscribe-form')
    // There is no better way to find this hidden input other than using magic
    // ids that may change on form input/rebuild
    this.$subscribeFiltersInput =
      this.$subscribeForm?.querySelector('input[value="{}"]') as HTMLInputElement | null

    // Observe zoom modifier key state
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Control' || event.key === 'Meta') {
        this.isZoomModifierKeyPressed = true
      }
    })

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Control' || event.key === 'Meta') {
        this.isZoomModifierKeyPressed = false
      }
    })

    window.addEventListener('blur', () => {
      this.isZoomModifierKeyPressed = false
    })

    // Bind items
    this.$itemsList = this.$element.querySelector('.section-listings__list') as HTMLElement

    // Load both GeoJSON files
    Promise.all([
      fetch('/content/themes/kierfchen-10/resources/data/cantons.geojson')
        .then(response => response.json()),
      fetch('/content/themes/kierfchen-10/resources/data/municipalities.geojson')
        .then(response => response.json())
    ])
      .then(([cantonsData, municipalitiesData]) => {
        this.initMap(cantonsData, municipalitiesData)
      })

    this.handleFilterChange()
    this.initializeList()
  }

  setFilters(filters: ListingFilters, sender?: ListingsFilter | undefined): void {
    this.filters = {
      ...this.filters,
      ...filters
    }
    this.handleFilterChange()

    if (this.listingsFilter !== null && sender !== this.listingsFilter) {
      this.listingsFilter.updateFilterPills()
    }
  }

  clearFilters(): void {
    this.setFilters({
      type: [],
      category: [],
      region: []
    })
  }

  getFilters(): ListingFilters {
    return this.filters
  }

  handleFilterChange(): void {
    const filters = this.getFilters()
    this.currentPage = 1
    this.updateItems()

    if (this.loadingListings) {
      this.filtersDirty = true
      return
    }

    this.loadingListings = true
    this.filtersDirty = false

    // TODO: Update selected regions on the map

    fetchListings(filters)
      .then(listings => this.setListings(listings))
      .finally(() => {
        this.loadingListings = false

        if (this.filtersDirty) {
          this.handleFilterChange()
        }
      })
  }

  private async updateItems(): Promise<void> {
    const filters = this.getFilters()
    const href = buildListingFilterQueryParams({
      ...filters,
      app_page: this.currentPage
    })
    if (href === this.lastListHref) {
      return
    }

    this.lastListHref = href
    const html = await fetch(href).then(response => response.text())
    const contentDocument = new DOMParser().parseFromString(html, 'text/html')

    const $newList = contentDocument.querySelector('.section-listings__list')
    if ($newList !== null) {
      this.$itemsList.innerHTML = $newList.innerHTML
      this.initializeList()
    }
  }

  private initializeList(): void {
    initializeListingTeasers(this.$itemsList)

    const $pageAnchors =
      [...this.$itemsList.querySelectorAll('a.pagination__page')] as HTMLAnchorElement[]
    for (const $pageAnchor of $pageAnchors) {
      $pageAnchor.addEventListener('click', (event) => {
        event.preventDefault()
        this.currentPage = Number($pageAnchor.innerText)
        this.updateItems()
      })
    }

    const $notifyEmailForm =
      this.$itemsList.querySelector('.section-listings__notify form')
    if ($notifyEmailForm !== null) {
      $notifyEmailForm.addEventListener('submit', (event) => {
        event.preventDefault()
        const formData = new FormData(event.target as HTMLFormElement)
        const email = formData.get('email') as string | null
        this.showSubscribeDialog(email ?? undefined)
      })
    }

    const $clearFiltersButton =
      this.$itemsList.querySelector('.section-listings__list-btn-clear')
    if ($clearFiltersButton !== null) {
      $clearFiltersButton.addEventListener('click', this.clearFilters.bind(this))
    }
  }

  setListings(listings: Listing[]) {
    this.listings = listings
    this.updateListingMarkers()
  }

  private updateListingMarkers() {
    if (this.markerLayer === null) {
      return
    }

    for (const listing of this.listings) {
      if (!listing.location_latitude || !listing.location_longitude) {
        continue
      }

      // Lazily create marker and add it to the map
      let marker = this.listingMarkerMap.get(listing.id)
      if (marker === undefined) {
        marker = this.createListingMarker(listing)
        this.listingMarkerMap.set(listing.id, marker)
        this.markerLayer.addLayer(marker)
      }

      // TODO: Manage marker visibility based on filters
    }

    // Remove dangling markers
    // TODO: Make state management more efficient
    for (const [id, marker] of this.listingMarkerMap.entries()) {
      if (!this.listings.some(listing => listing.id === id)) {
        this.markerLayer.removeLayer(marker)
        this.listingMarkerMap.delete(id)
      }
    }
  }

  private updateZoomButtons() {
    if (this.map === null) {
      return
    }

    const currentZoom = this.map.getZoom()
    const isMaxZoomedOut = currentZoom <= this.map.getMinZoom()

    if (this.$zoomInControl !== null) {
      this.$zoomInControl.disabled = currentZoom >= this.map.getMaxZoom()
    }

    if (this.$zoomOutControl !== null) {
      this.$zoomOutControl.disabled = isMaxZoomedOut
    }

    this.$element.classList.toggle('section-listings--max-zoom', isMaxZoomedOut)
  }

  private createListingMarkerIcon(
    listing: Listing
  ): ReturnType<typeof L.divIcon> {
    const key = `${listing.icon}-${listing.primary_category.color!}`
    if (this.iconsMap[key] === undefined) {
      this.iconsMap[key] = L.divIcon({
        className:
          'section-listings__map-marker ' +
          'section-listings__map-marker--' + listing.primary_category.color! + ' ' +
          'section-listings__map-marker--' + listing.icon,
        html: iconSvgMap[listing.icon],
        iconSize: [16, 16],
        iconAnchor: [8, 8],
        popupAnchor: [0, -8],
        tooltipAnchor: [0, -8]
      })
    }
    return this.iconsMap[key]
  }

  private createListingMarker(listing: Listing): ListingMarker {
    const coordinates: [number, number] = [
      parseFloat(listing.location_latitude),
      parseFloat(listing.location_longitude)
    ]

    const markerTitle = listing.categories.length === 1
      ? listing.categories[0].name
      : listing.primary_category.name

    const marker = L.marker(coordinates, {
      alt: markerTitle,
      icon: this.createListingMarkerIcon(listing),
      primaryCategoryColor: listing.primary_category.color!
    } as ListingMarkerOptions)
      .bindTooltip(markerTitle, {
        permanent: false,
        direction: 'top',
        className:
          'section-listings__map-marker-tooltip ' +
          'section-listings__map-marker-tooltip--' + listing.primary_category.color!
      })
      .on('click', () => {
        this.showListingDetailDialog(listing)
      })

    return marker as ListingMarker
  }

  private composeRegionClassName(feature?: GeoJSON.Feature): string {
    let className = 'leaflet-interactive section-listings__path-region'

    if (feature?.properties !== undefined) {
      const commune = (feature.properties as { COMMUNE?: unknown }).COMMUNE
      if (typeof commune === 'string' && this.filters.region?.includes(commune)) {
        className += ' section-listings__path-region--selected'
      }
    }

    return className
  }

  /**
   * Create a layer group for a region, given a GeoJSON feature collection.
   */
  private createRegionLayer(
    collection: GeoJSON.FeatureCollection
  ): L.FeatureGroup {
    const layers = []

    const baseLayer = L.geoJSON(collection, {
      style: (feature) => ({ className: this.composeRegionClassName(feature) }),
      onEachFeature: (feature, layer: L.Path) => {
        // Determine the region name
        let name: string | null = null
        let selectable = false
        if (
          'COMMUNE' in feature.properties &&
          typeof feature.properties.COMMUNE === 'string'
        ) {
          name = feature.properties.COMMUNE
          selectable = true
        } else if (
          'CANTON' in feature.properties &&
          typeof feature.properties.CANTON === 'string'
        ) {
          name = feature.properties.CANTON
        }

        // Determine the region outer polygon coordinates
        let outerCoordinates: Position[] | null = null
        if (feature.geometry.type === 'MultiPolygon') {
          outerCoordinates = feature.geometry.coordinates[0][0]
        }
        if (feature.geometry.type === 'Polygon') {
          outerCoordinates = feature.geometry.coordinates[0]
        }

        if (name !== null && outerCoordinates !== null) {
          const centroid = calculateCentroid(outerCoordinates)
          const regionLabelMarker = L.marker(centroid, {
            icon: L.divIcon({
              className: 'section-listings__map-region-label',
              html: `<span class="section-listings__map-region-label-text">${name}</span>`,
              iconSize: L.point(170, 24),
              iconAnchor: [85, 0],
            }),
            interactive: false
          })

          layers.push(regionLabelMarker)

          layer.on('click', () => {
            if (this.map !== null) {
              // TODO: Fix this type error
              /** @ts-ignore */
              const bounds = layer.getBounds()
              this.map.fitBounds(bounds, {
                padding: [0, 0],
                maxZoom: this.map.getMaxZoom(),
              })

              // Allow users to select regions on the map
              if (selectable) {
                const selected = !this.filters.region?.includes(name)
                if (selected) {
                  // Add region to filters
                  this.setFilters({
                    region: [...(this.filters.region ?? []), name]
                  })
                } else {
                  // Remove region from filters
                  this.setFilters({
                    region: this.filters.region?.filter(region => region !== name)
                  })
                }

                // TODO: Move this to an updateRegionSelection method
                // Update region selection on the map
                // For some reason, we need to both call setStyle and set the
                // class name ourselves to ensure the layer is updated and
                // will again update when zooming out and back in.
                const layerClassName = this.composeRegionClassName(feature)
                const element = layer.getElement()
                if (element !== undefined) {
                  element.classList = layerClassName
                }
                layer.setStyle({
                  className: layerClassName
                })

                // Update listings
                this.handleFilterChange()
              }
            }
          })
        }
      },
    })

    layers.unshift(baseLayer)
    return L.featureGroup(layers)
  }

  /**
   * Initialize the map with the given cantons and municipalities GeoJSON
   * feature collections.
   */
  async initMap(
    cantonsCollection: GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon>,
    municipalitiesCollection: GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon>
  ) {
    const $map: HTMLDivElement | null =
      this.$element.querySelector('.section-listings__leaflet')

    if ($map === null) {
      return
    }

    const cantonFeatureGroup = this.createRegionLayer(cantonsCollection)
    const municipalityFeatureGroup = this.createRegionLayer(municipalitiesCollection)

    // Derive the map bounds from the canton layer
    const bounds = cantonFeatureGroup.getBounds().pad(0.1)

    const minCantonLayerZoomLevel = window.innerWidth < 1536 ? 9 : 9.5
    const minMunicipalityLayerZoomLevel = window.innerWidth < 1536 ? 10.5 : 11
    const maxMunicipalityLayerZoomLevel = 12

    // Initialize the map
    const zoomDelta = 1
    this.map = L.map($map, {
      scrollWheelZoom: false,
      attributionControl: false,
      layers: [cantonFeatureGroup],
      zoomControl: false,
      zoom: minCantonLayerZoomLevel,
      zoomSnap: 0.5,
      zoomDelta: zoomDelta,
      minZoom: minCantonLayerZoomLevel,
      maxZoom: maxMunicipalityLayerZoomLevel,
      maxBounds: bounds
    }).fitBounds(bounds)

    setupCooperativeGestures(this.map)

    // TODO: Add marker clustering if needed
    this.markerLayer = new L.MarkerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 32,
      animate: true,
      iconCreateFunction: (cluster: L.MarkerCluster) => {
        const children = cluster.getAllChildMarkers() as ListingMarker[]
        const firstColor = children[0]?.options.primaryCategoryColor ?? ''
        const allSameColor = children.every(child => child.options.primaryCategoryColor === firstColor)

        // Determine the cluster css classes depending if all markers are of the same color or not
        const clusterClassName = allSameColor
          ? `section-listings__map-cluster section-listings__map-cluster--${firstColor}`
          : 'section-listings__map-cluster'

        let childCount = cluster.getChildCount()
        return new L.DivIcon({
          html: '<span>' + childCount + '</span>',
          className: clusterClassName,
          iconSize: new L.Point(32, 32)
        })
      }
    })
    this.map.addLayer(this.markerLayer)

    // Add our custom wheel event listener
    this.map.getContainer().addEventListener('wheel', (event) => {
      if (!('deltaY' in event) || event.deltaY === 0) {
        return
      }

      if (this.isZoomModifierKeyPressed) {
        event.preventDefault()
        this.setScrollMessageVisible(false)

        const delta = event.deltaY
        const relativeDelta = delta > 0 ? -zoomDelta : zoomDelta

        if (this.map) {
          const currentZoom = this.map.getZoom()
          const newZoom = Math.max(
            this.map.getMinZoom(),
            Math.min(this.map.getMaxZoom(), currentZoom + relativeDelta)
          )
          this.map.setZoom(newZoom)
        }
      } else {
        this.setScrollMessageVisible(true)
      }
    })

    // Switch between cantons and municipalities layers when zooming
    this.map.on('zoomend', () => {
      const currentZoom = this.map!.getZoom()
      if (currentZoom >= minMunicipalityLayerZoomLevel) {
        cantonFeatureGroup.remove()
        municipalityFeatureGroup.addTo(this.map!)
      } else {
        municipalityFeatureGroup.remove()
        cantonFeatureGroup.addTo(this.map!)
      }

      this.setScrollMessageVisible(false)
      this.updateZoomButtons()
    })

    this.updateListingMarkers()
    this.updateZoomButtons()
  }

  showListingDetailDialog(listing: Listing) {
    const dialog = new Dialog()
    dialog.setContentUrl(listing.url, true)
    dialog.setVisible(true)
  }

  showSubscribeDialog(email?: string) {
    if (this.$subscribeFiltersInput !== null) {
      const filters = this.getFilters()
      this.$subscribeFiltersInput.value = JSON.stringify(filters)
    }
    if (this.$subscribeForm !== null) {
      // Set the email input value if it is provided
      if (email !== undefined) {
        const $emailInput =
          this.$subscribeForm.querySelector('input[type="email"]') as HTMLInputElement | null
        if ($emailInput !== null) {
          $emailInput.value = email
        }
      }

      const dialog = new Dialog()
      dialog.setContent(this.$subscribeForm, false)
      dialog.setVisible(true)
    }
  }

  private setScrollMessageVisible(visible: boolean) {
    if ((this.scrollMessageTimeout !== null) !== visible) {
      if (visible) {
        // Show message
        this.$element.classList.add('section-listings--scroll-message-visible')
        this.scrollMessageTimeout = window.setTimeout(() => {
          this.setScrollMessageVisible(false)
          this.scrollMessageTimeout = null
        }, 2000)
      } else {
        // Hide message
        if (this.scrollMessageTimeout !== null) {
          clearTimeout(this.scrollMessageTimeout)
          this.scrollMessageTimeout = null
        }
        this.$element.classList.remove('section-listings--scroll-message-visible')
      }
    }
  }
}
